import React, { useState, useEffect } from 'react';
import { collection, getDocs, addDoc, doc, updateDoc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { 
  Container, Paper, Typography, TextField, Button, Grid, 
  MenuItem, Box, Tabs, Tab, InputAdornment, Alert, Snackbar 
} from '@mui/material';
import { AccountBalance, MoneyOff, Savings, Send, RequestQuote } from '@mui/icons-material';
import FloatingCalculator from './FloatingCalculator';

function LancamentoRapido({ user }) {
  const [aba, setAba] = useState(0); // 0=Banco, 1=Despesa, 2=Caixinha, 3=Empréstimo
  
  const [bancos, setBancos] = useState([]);
  const [caixinhas, setCaixinhas] = useState([]);
  const [listaDespesas, setListaDespesas] = useState([]);
  const [listaEmprestimos, setListaEmprestimos] = useState([]);
  const [contaSelecionada, setContaSelecionada] = useState(null);
  
  const [sucesso, setSucesso] = useState(false);
  const [mensagem, setMensagem] = useState("");

  // Formulário Unificado
  const [selecionadoId, setSelecionadoId] = useState('default'); 
  const [tipoOperacao, setTipoOperacao] = useState('saida');
  const [valorFormatado, setValorFormatado] = useState('');
  const [titulo, setTitulo] = useState('');
  const [descricao, setDescricao] = useState('');
  const [data, setData] = useState(new Date().toISOString().split('T')[0]);
  const [numParcelasDespesa, setNumParcelasDespesa] = useState(1);
  
  // Formulário Novo Empréstimo
  const [novoEmprestimo, setNovoEmprestimo] = useState({
    pessoa: '', descricao: '', valor: '', dataSaida: new Date().toISOString().split('T')[0],
    dataVencimento: '', status: 'pendente', juros: '', notas: '', numParcelas: 1
  });

  useEffect(() => {
    const carregarListas = async () => {
      if (!user || !user.uid) return;
      // Bancos, Caixinhas
      const bSnap = await getDocs(collection(db, `users/${user.uid}/bancos`));
      const listaBancos = bSnap.docs.map(d => ({ id: d.id, ...d.data() }));
      setBancos(listaBancos);
      if (selecionadoId === 'default') {
        const pref = listaBancos.find((b) => b.is_preferencial);
        if (pref) setSelecionadoId(pref.id);
        else if (listaBancos[0]) setSelecionadoId(listaBancos[0].id);
      }
      const cSnap = await getDocs(collection(db, `users/${user.uid}/investimentos`));
      setCaixinhas(cSnap.docs.map(d => ({ id: d.id, ...d.data() })));

      // Despesas ativas
      const dSnap = await getDocs(collection(db, `users/${user.uid}/gastos_mensais`));
      setListaDespesas(dSnap.docs.map(d => ({ id: d.id, ...d.data(), type: 'gasto' })).filter(c => c.status === 'pendente' || c.status === 'parcial'));

      // Empréstimos ativos
      const eSnap = await getDocs(collection(db, `users/${user.uid}/emprestimos`));
      setListaEmprestimos(eSnap.docs.map(d => ({ id: d.id, ...d.data(), type: 'emprestimo' })).filter(c => c.status === 'pendente' || c.status === 'parcial'));
    };
    carregarListas();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const handleValorChange = (e, setter, field) => {
    let value = e.target.value.replace(/\D/g, "");
    const res = new Intl.NumberFormat('pt-BR', { minimumFractionDigits: 2 }).format(parseFloat(value) / 100);
    const finalValue = res === "NaN" ? "" : res;
    if (setter) {
      setter(prev => ({...prev, [field]: finalValue}));
    } else {
      setValorFormatado(finalValue);
    }
  };

  const limparFormulario = (msg) => {
    setSelecionadoId('default'); 
    setValorFormatado(''); 
    setTitulo(''); 
    setDescricao('');
    setData(new Date().toISOString().split('T')[0]);
    setNumParcelasDespesa(1);
    setContaSelecionada(null);
    setNovoEmprestimo({
      pessoa: '', descricao: '', valor: '', dataSaida: new Date().toISOString().split('T')[0],
      dataVencimento: '', status: 'pendente', juros: '', notas: '', numParcelas: 1
    });
    setSucesso(true);
    setMensagem(msg);
  };

  const pagarContaVinculada = async (conta, valorPago) => {
    const isGasto = conta.type === 'gasto';
    const collectionName = isGasto ? 'gastos_mensais' : 'emprestimos';
    const contaRef = doc(db, `users/${user.uid}/${collectionName}`, conta.id);
  
    if (!conta.parcelas || conta.parcelas.length <= 1) {
      await updateDoc(contaRef, { status: isGasto ? 'pago' : 'recebido', valorPago: (conta.valorPago || 0) + valorPago });
      return;
    }
  
    const novasParcelas = [...conta.parcelas];
    let primeiraParcelaNaoPagaIdx = novasParcelas.findIndex(p => !p.paga);
  
    if (primeiraParcelaNaoPagaIdx !== -1) {
      novasParcelas[primeiraParcelaNaoPagaIdx].paga = true;
      novasParcelas[primeiraParcelaNaoPagaIdx].dataPagamento = new Date().toISOString().split('T')[0];
  
      const parcelasPagasCount = novasParcelas.filter(p => p.paga).length;
      const novoStatus = parcelasPagasCount === novasParcelas.length ? (isGasto ? 'pago' : 'recebido') : 'parcial';
      const valorTotalPago = novasParcelas.reduce((acc, p) => p.paga ? acc + p.valor : acc, 0);
  
      await updateDoc(contaRef, {
        parcelas: novasParcelas,
        status: novoStatus,
        [isGasto ? 'valorPago' : 'valorRecebido']: valorTotalPago
      });
    }
  };

  const salvarLancamento = async () => {
    const valorNum = parseFloat(valorFormatado.replace(/\./g, '').replace(',', '.'));

    // Lançamento em Banco
    if (aba === 0) {
      if (selecionadoId === 'default' || !valorNum) return;
      const vFinal = tipoOperacao === 'entrada' ? valorNum : -valorNum;
      await addDoc(collection(db, `users/${user.uid}/bancos/${selecionadoId}/movimentacoes`), { 
        valor: vFinal, titulo, descricao, data, contaId: contaSelecionada?.id || null
      });
      const bancoRef = doc(db, `users/${user.uid}/bancos/${selecionadoId}`);
      const bancoSnap = await getDoc(bancoRef);
      await updateDoc(bancoRef, { saldo: (bancoSnap.data().saldo || 0) + vFinal });

      if (contaSelecionada) {
        await pagarContaVinculada(contaSelecionada, valorNum);
      }
      limparFormulario("Lançamento no banco salvo!");
    
    // Lançamento de Despesa Rápida
    } else if (aba === 1) {
      if (!titulo || !valorNum || !data) return;
      const numParcelas = parseInt(numParcelasDespesa, 10) || 1;
      const valorParcela = valorNum / numParcelas;
      const parcelas = Array.from({ length: numParcelas }, (_, i) => ({
        numero: i + 1,
        valor: valorParcela,
        dataParcela: new Date(new Date(data).getTime() + (i * 30 * 24 * 60 * 60 * 1000)).toISOString().split('T')[0],
        paga: false,
        dataPagamento: null
      }));

      await addDoc(collection(db, `users/${user.uid}/gastos_mensais`), { 
        nome: titulo.trim(),
        valor: valorNum,
        dataVencimento: data,
        status: 'pendente',
        numParcelas,
        parcelas,
        valorPago: 0,
        dataCriacao: new Date()
      });
      limparFormulario("Nova despesa rápida salva!");

    // Lançamento em Caixinha
    } else if (aba === 2) {
      if (selecionadoId === 'default' || !valorNum) return;
      const caixinhaRef = doc(db, `users/${user.uid}/investimentos/${selecionadoId}`);
      const caixinhaSnap = await getDoc(caixinhaRef);
      const saldoAtual = caixinhaSnap.data().saldoAtual || 0;
      const novoSaldo = tipoOperacao === 'deposito' ? saldoAtual + valorNum : saldoAtual - valorNum;
      
      await updateDoc(caixinhaRef, { saldoAtual: novoSaldo });
      await addDoc(collection(db, `users/${user.uid}/investimentos/${selecionadoId}/historico`), {
        tipo: tipoOperacao, valor: valorNum, descricao: titulo || (tipoOperacao === 'deposito' ? 'Aporte' : 'Resgate'), data: new Date().toISOString()
      });
      limparFormulario("Movimentação na caixinha salva!");

    // Lançamento de Novo Empréstimo
    } else if (aba === 3) {
      const valorEmprestimo = parseFloat(novoEmprestimo.valor.replace(/\./g, '').replace(',', '.'));
      if (!novoEmprestimo.pessoa || !valorEmprestimo || !novoEmprestimo.dataVencimento) return;

      const numParcelas = parseInt(novoEmprestimo.numParcelas) || 1;
      const valorParcela = valorEmprestimo / numParcelas;
      const parcelas = Array.from({ length: numParcelas }, (_, i) => ({
        numero: i + 1, valor: valorParcela, paga: false, dataPagamento: null,
        dataParcela: new Date(new Date(novoEmprestimo.dataVencimento).getTime() + (i * 30 * 24 * 60 * 60 * 1000)).toISOString().split('T')[0]
      }));

      await addDoc(collection(db, `users/${user.uid}/emprestimos`), {
        ...novoEmprestimo, valor: valorEmprestimo, parcelas, numParcelas,
        juros: novoEmprestimo.juros ? parseFloat(novoEmprestimo.juros) : 0, valorRecebido: 0, dataCriacao: new Date()
      });
      limparFormulario("Novo empréstimo salvo!");
    }
  };

  const handleSelectConta = (id, tipo) => {
    const lista = tipo === 'gasto' ? listaDespesas : listaEmprestimos;
    const conta = lista.find(c => c.id === id);
    if (conta) {
      setContaSelecionada(conta);
      setTitulo(conta.type === 'gasto' ? conta.nome : conta.pessoa);
      const valorParcela = (conta.parcelas && conta.parcelas.find(p => !p.paga)?.valor) || conta.valor;
      setValorFormatado(new Intl.NumberFormat('pt-BR', { minimumFractionDigits: 2 }).format(valorParcela));
    } else {
      setContaSelecionada(null);
      setTitulo('');
      setValorFormatado('');
    }
  };

  const handleChangeAba = (_, novaAba) => {
    setAba(novaAba);
    if (novaAba === 2) {
      setTipoOperacao((prev) => (prev === 'deposito' || prev === 'resgate' ? prev : 'deposito'));
      return;
    }
    setTipoOperacao((prev) => (prev === 'entrada' || prev === 'saida' ? prev : 'saida'));
  };
  
  return (
    <Container maxWidth="md">
      <FloatingCalculator />
      <Snackbar open={sucesso} autoHideDuration={3000} onClose={() => setSucesso(false)} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
        <Alert severity="success" sx={{ width: '100%' }}>{mensagem}</Alert>
      </Snackbar>

      <Typography variant="h5" sx={{ mb: 3, fontWeight: 'bold', color: '#1a237e' }}>Lançamento Rápido</Typography>

      <Paper elevation={3} sx={{ borderRadius: 4, overflow: 'hidden' }}>
        <Tabs value={aba} onChange={handleChangeAba} variant="fullWidth" sx={{ bgcolor: '#f5f5f5', borderBottom: '1px solid #e0e0e0' }}>
          <Tab icon={<AccountBalance />} label="Banco" />
          <Tab icon={<MoneyOff />} label="Despesa" />
          <Tab icon={<Savings />} label="Caixinha" />
          <Tab icon={<RequestQuote />} label="Empréstimo" />
        </Tabs>

        <Box sx={{ p: { xs: 2, sm: 3 } }}>
          {/* --- ABA BANCO --- */}
          {aba === 0 && <Grid container spacing={2}>
            <Grid item xs={12} sm={6}><TextField select fullWidth label="Selecione o Banco" value={selecionadoId} onChange={e => setSelecionadoId(e.target.value)} InputLabelProps={{ shrink: true }}><MenuItem value="default" disabled>Escolha um banco...</MenuItem>{bancos.map(b => <MenuItem key={b.id} value={b.id}>{b.nome}</MenuItem>)}</TextField></Grid>
            <Grid item xs={12} sm={6}><TextField select fullWidth label="Tipo de Operação" value={tipoOperacao} onChange={e => setTipoOperacao(e.target.value)} InputLabelProps={{ shrink: true }}><MenuItem value="saida">Saída (Gasto)</MenuItem><MenuItem value="entrada">Entrada (Receita)</MenuItem></TextField></Grid>
            
            {tipoOperacao === 'saida' && <Grid item xs={12}><TextField select fullWidth label="Vincular à Despesa (Opcional)" value={contaSelecionada?.id || 'default'} onChange={e => handleSelectConta(e.target.value, 'gasto')} InputLabelProps={{ shrink: true }}><MenuItem value="default">Nenhuma</MenuItem>{listaDespesas.map(c => <MenuItem key={c.id} value={c.id}>{c.nome}</MenuItem>)}</TextField></Grid>}
            {tipoOperacao === 'entrada' && <Grid item xs={12}><TextField select fullWidth label="Vincular ao Empréstimo (Opcional)" value={contaSelecionada?.id || 'default'} onChange={e => handleSelectConta(e.target.value, 'emprestimo')} InputLabelProps={{ shrink: true }}><MenuItem value="default">Nenhum</MenuItem>{listaEmprestimos.map(c => <MenuItem key={c.id} value={c.id}>{c.pessoa}</MenuItem>)}</TextField></Grid>}

            <Grid item xs={12} sm={8}><TextField fullWidth label="Título do Lançamento" value={titulo} onChange={e => setTitulo(e.target.value)} /></Grid>
            <Grid item xs={12} sm={4}><TextField fullWidth label="Valor" value={valorFormatado} onChange={e => handleValorChange(e)} InputProps={{ startAdornment: <InputAdornment position="start">R$</InputAdornment> }} /></Grid>
            <Grid item xs={12} sm={6}><TextField fullWidth type="date" label="Data do Lançamento" value={data} onChange={e => setData(e.target.value)} InputLabelProps={{ shrink: true }} /></Grid>
            <Grid item xs={12}><TextField fullWidth multiline rows={2} label="Descrição Detalhada (Opcional)" value={descricao} onChange={e => setDescricao(e.target.value)} /></Grid>
          </Grid>}
          
          {/* --- ABA DESPESA --- */}
          {aba === 1 && <Grid container spacing={2}>
            <Grid item xs={12}><TextField fullWidth label="Nome da Despesa" value={titulo} onChange={e => setTitulo(e.target.value)} /></Grid>
            <Grid item xs={12} sm={6}><TextField fullWidth label="Valor" value={valorFormatado} onChange={e => handleValorChange(e)} InputProps={{ startAdornment: <InputAdornment position="start">R$</InputAdornment> }} /></Grid>
            <Grid item xs={12} sm={6}><TextField fullWidth type="date" label="Vencimento" value={data} onChange={e => setData(e.target.value)} InputLabelProps={{ shrink: true }} /></Grid>
            <Grid item xs={12}><TextField fullWidth type="number" label="Nº de Parcelas" value={numParcelasDespesa} onChange={(e) => setNumParcelasDespesa(e.target.value)} inputProps={{ min: 1 }} /></Grid>
          </Grid>}
          
          {/* --- ABA CAIXINHA --- */}
          {aba === 2 && <Grid container spacing={2}>
            <Grid item xs={12} sm={6}><TextField select fullWidth label="Selecione a Caixinha" value={selecionadoId} onChange={e => setSelecionadoId(e.target.value)} InputLabelProps={{ shrink: true }}><MenuItem value="default" disabled>Escolha uma caixinha...</MenuItem>{caixinhas.map(c => <MenuItem key={c.id} value={c.id}>{c.nome}</MenuItem>)}</TextField></Grid>
            <Grid item xs={12} sm={6}><TextField select fullWidth label="Tipo de Operação" value={tipoOperacao} onChange={e => setTipoOperacao(e.target.value)} InputLabelProps={{ shrink: true }}><MenuItem value="deposito">Guardar Valor</MenuItem><MenuItem value="resgate">Resgatar Valor</MenuItem></TextField></Grid>
            <Grid item xs={12} sm={8}><TextField fullWidth label="Descrição (Opcional)" value={titulo} onChange={e => setTitulo(e.target.value)} /></Grid>
            <Grid item xs={12} sm={4}><TextField fullWidth label="Valor" value={valorFormatado} onChange={e => handleValorChange(e)} InputProps={{ startAdornment: <InputAdornment position="start">R$</InputAdornment> }} /></Grid>
          </Grid>}

          {/* --- ABA EMPRÉSTIMO --- */}
          {aba === 3 && <Grid container spacing={2}>
            <Grid item xs={12}><TextField fullWidth label="Nome da Pessoa" value={novoEmprestimo.pessoa} onChange={(e) => setNovoEmprestimo({...novoEmprestimo, pessoa: e.target.value})} /></Grid>
            <Grid item xs={12}><TextField fullWidth label="Descrição" value={novoEmprestimo.descricao} onChange={(e) => setNovoEmprestimo({...novoEmprestimo, descricao: e.target.value})} /></Grid>
            <Grid item xs={12} sm={6}><TextField fullWidth label="Valor" value={novoEmprestimo.valor} onChange={(e) => handleValorChange(e, setNovoEmprestimo, 'valor')} InputProps={{ startAdornment: <InputAdornment position="start">R$</InputAdornment> }} /></Grid>
            <Grid item xs={12} sm={6}><TextField fullWidth type="number" label="Nº de Parcelas" value={novoEmprestimo.numParcelas} onChange={(e) => setNovoEmprestimo({...novoEmprestimo, numParcelas: e.target.value})} inputProps={{ min: 1 }} /></Grid>
            <Grid item xs={12} sm={6}><TextField fullWidth type="date" label="Data de Vencimento" value={novoEmprestimo.dataVencimento} onChange={(e) => setNovoEmprestimo({...novoEmprestimo, dataVencimento: e.target.value})} InputLabelProps={{ shrink: true }} /></Grid>
            <Grid item xs={12} sm={6}><TextField fullWidth label="Juros (%)" type="number" value={novoEmprestimo.juros} onChange={(e) => setNovoEmprestimo({...novoEmprestimo, juros: e.target.value})} /></Grid>
            <Grid item xs={12}><TextField fullWidth multiline rows={2} label="Notas" value={novoEmprestimo.notas} onChange={(e) => setNovoEmprestimo({...novoEmprestimo, notas: e.target.value})} /></Grid>
          </Grid>}

          {/* Botão Salvar Universal */}
          <Box sx={{ mt: 3 }}>
            <Button fullWidth variant="contained" size="large" onClick={salvarLancamento} startIcon={<Send />} sx={{ bgcolor: '#1a237e', height: 50, fontWeight: 'bold', '&:hover': { bgcolor: '#0d1440' } }}>
              SALVAR
            </Button>
          </Box>
        </Box>
      </Paper>
    </Container>
  );
}

export default LancamentoRapido;
